import { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, increment, deleteDoc, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { DocumentTag } from '../types/documentTags';

export function useDocumentTags() {
  const [tags, setTags] = useState<DocumentTag[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const organizationId = user?.organizationId;

  // Load all tags for the organization
  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    const loadTags = async () => {
      try {
        const tagsQuery = query(
          collection(db, 'documentTags'),
          orderBy('count', 'desc'),
          limit(100)
        );
        
        const snapshot = await getDocs(tagsQuery);
        const tagList = snapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date()
            } as DocumentTag;
          })
          .filter(tag => tag.organizationId === organizationId);

        setTags(tagList);
      } catch (error) {
        console.error('Error loading document tags:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTags();
  }, [organizationId]);

  // Add or increment tag count
  const addTag = async (tagName: string): Promise<string> => {
    if (!organizationId) throw new Error('Organization ID required');

    const tagId = `${organizationId}_${tagName.toLowerCase().replace(/\s+/g, '_')}`;
    
    try {
      const tagRef = doc(db, 'documentTags', tagId);
      
      // Check if tag exists
      const existingTag = tags.find(t => t.id === tagId);
      
      if (existingTag) {
        // Increment existing tag count
        await updateDoc(tagRef, {
          count: increment(1),
          updatedAt: new Date()
        });
        
        // Update local state
        setTags(prev => prev.map(tag => 
          tag.id === tagId 
            ? { ...tag, count: tag.count + 1, updatedAt: new Date() }
            : tag
        ));
      } else {
        // Create new tag
        const newTag: Omit<DocumentTag, 'id'> = {
          name: tagName,
          title: tagName, // Use the same value for both name and title initially
          count: 1,
          color: generateTagColor(),
          createdAt: new Date(),
          updatedAt: new Date(),
          organizationId
        };
        
        await setDoc(tagRef, newTag);
        
        // Update local state
        setTags(prev => [...prev, { id: tagId, ...newTag }]);
      }
      
      return tagId;
    } catch (error) {
      console.error('Error adding tag:', error);
      throw error;
    }
  };

  // Decrement tag count (when removing from document)
  const decrementTag = async (tagName: string): Promise<void> => {
    if (!organizationId) return;

    const tagId = `${organizationId}_${tagName.toLowerCase().replace(/\s+/g, '_')}`;
    
    try {
      const tagRef = doc(db, 'documentTags', tagId);
      const existingTag = tags.find(t => t.id === tagId);
      
      if (existingTag) {
        if (existingTag.count <= 1) {
          // Remove tag if count would be 0
          await deleteDoc(tagRef);
          setTags(prev => prev.filter(tag => tag.id !== tagId));
        } else {
          // Decrement count
          await updateDoc(tagRef, {
            count: increment(-1),
            updatedAt: new Date()
          });
          
          setTags(prev => prev.map(tag => 
            tag.id === tagId 
              ? { ...tag, count: tag.count - 1, updatedAt: new Date() }
              : tag
          ));
        }
      }
    } catch (error) {
      console.error('Error decrementing tag:', error);
      throw error;
    }
  };

  // Sync tags from documents (utility function to rebuild tag counts)
  const syncTagsFromDocuments = async (documents: any[]): Promise<void> => {
    if (!organizationId) return;

    try {
      // Count all tags from documents
      const tagCounts = new Map<string, number>();
      
      documents.forEach(doc => {
        doc.tags?.forEach((tag: string) => {
          tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        });
      });

      // Update or create tags in Firestore
      const batch = [];
      
      for (const [tagName, count] of Array.from(tagCounts.entries())) {
        const tagId = `${organizationId}_${tagName.toLowerCase().replace(/\s+/g, '_')}`;
        const tagRef = doc(db, 'documentTags', tagId);
        
        batch.push(
          setDoc(tagRef, {
            name: tagName,
            count,
            color: generateTagColor(),
            createdAt: new Date(),
            updatedAt: new Date(),
            organizationId
          }, { merge: true })
        );
      }

      // Execute all updates
      await Promise.all(batch);
      
      // Reload tags
      const tagsQuery = query(
        collection(db, 'documentTags'),
        orderBy('count', 'desc')
      );
      
      const snapshot = await getDocs(tagsQuery);
      const tagList = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          } as DocumentTag;
        })
        .filter(tag => tag.organizationId === organizationId);

      setTags(tagList);
      
    } catch (error) {
      console.error('Error syncing tags:', error);
      throw error;
    }
  };

  // Generate a color for new tags
  const generateTagColor = (): string => {
    const colors = [
      '#3b82f6', // blue
      '#10b981', // emerald
      '#f59e0b', // amber
      '#ef4444', // red
      '#8b5cf6', // violet
      '#06b6d4', // cyan
      '#84cc16', // lime
      '#f97316', // orange
      '#ec4899', // pink
      '#6366f1'  // indigo
    ];
    
    return colors[Math.floor(Math.random() * colors.length)];
  };

  return {
    tags,
    loading,
    addTag,
    decrementTag,
    syncTagsFromDocuments
  };
}
